import { computed, ref } from "vue";
import type { ComputedRef, Ref } from "vue";
import { FileValidationErrorType } from "@plugins/file-uploader-v2/file.uploader.constant.ts";
import {
  FileType,
  FileStatus,
  FileTypeMap,
  Dimensions,
  UploadFlowFileTypeMap,
  FileResponse,
  UploadFlowValues,
  FileStatusValues,
  FileUploadEntry,
  FileTypeValues,
  FileTypeOrder,
} from "@plugins/file-uploader-v2/types.ts";
import { MEDIA_TYPE, MediaType } from "@plugins/media/media.constant";
import PollingUtils from "@utils/polling.utils.ts";
import FileUploadApi from "@apis/file.upload.api.ts";
import appConfigs from "@configs/app";
import { useI18n } from "vue-i18n";
import StringUtils from "@utils/string.utils.ts";
import DeviceUtils from "@utils/device.utils.ts";

interface ScanResultResponse {
  code: string;
  data?: {
    status: FileStatusValues;
    fileName?: string;
  };
}

export function getFileExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() || "";
}

export function getExtensionsByUploadFlow(flow: UploadFlowValues): string[] {
  const fileTypes = UploadFlowFileTypeMap[flow] || [];

  if (fileTypes.length === 1) {
    return FileTypeMap[fileTypes[0]]?.ext || [];
  }

  return [...new Set(fileTypes.flatMap((type) => FileTypeMap[type]?.ext || []))];
}

export function useAcceptedFileExtensions(
  uploadFlow: Readonly<Ref<UploadFlowValues>>,
): ComputedRef<string> {
  return computed(() =>
    DeviceUtils.isIos()
      ? ""
      : getExtensionsByUploadFlow(uploadFlow.value)
          .map((extension) => (extension.startsWith(".") ? extension : `.${extension}`))
          .join(","),
  );
}

export function getSuccessFileUrl(fileUuid: string, fileName: string) {
  return `${appConfigs.s3ResourceDomain}/success/${fileUuid}/${fileName}`;
}

export function cleanFileName(fileName: string, replaceWith = "_") {
  // normalize whitespace
  fileName = fileName.replace(/[\s\u00A0\u202F]+/g, " ");
  // replace whitespace & special characters
  fileName = fileName.replaceAll(/[\\\/(){}\[\]':;+%#^@=]/g, replaceWith);
  return fileName.replaceAll(" ", "_");
}

export function formatFileSize(size: number) {
  // Dropzone file size checking
  let cutoff, i, selectedSize, selectedUnit, unit, units, _i, _len;
  selectedSize = 0;
  selectedUnit = "b";
  if (size > 0) {
    units = ["TB", "GB", "MB", "KB", "b"];
    for (i = _i = 0, _len = units.length; _i < _len; i = ++_i) {
      unit = units[i];
      cutoff = Math.pow(1024, 4 - i) / 10; // 1024 - SLS default file size base
      if (size >= cutoff) {
        selectedSize = size / Math.pow(1024, 4 - i);
        selectedUnit = unit;
        break;
      }
    }
    selectedSize = Math.round(10 * selectedSize) / 10;
  }
  return selectedSize + " " + selectedUnit;
}

/**
 * Pure upload pipeline: generate signed URL → S3 PUT → poll virus scan.
 * Returns a FileResponse with status READY (including uuid, name, url) on success,
 * or status FAILED on any error. No Vue context required.
 */
export async function uploadFileToS3(
  file: File,
  flow: UploadFlowValues,
  options?: { recordType?: string; recordUuid?: string },
): Promise<{ status: string; uuid?: string; name?: string; url?: string }> {
  const payload = {
    contentType: file.type,
    uploadFlow: flow,
    filename: file.name.replace(/[\s\u00A0\u202F]+/g, "_"),
    recordType: options?.recordType,
    recordUuid: options?.recordUuid,
  };

  try {
    const fileUrlResponse = await FileUploadApi.generateFileUploadUrl(payload);
    if (fileUrlResponse.code !== "ok" || !fileUrlResponse.data) {
      return { status: FileStatus.FAILED };
    }

    const { fileUuid, signedUploadUrl } = fileUrlResponse.data;
    await FileUploadApi.uploadFile(signedUploadUrl, file);

    const scanResultResponse = (await pollScanningStatus(fileUuid)) as ScanResultResponse;
    if (scanResultResponse.code === "ok" && scanResultResponse.data?.status === FileStatus.READY) {
      const fileName = scanResultResponse.data.fileName;
      return {
        status: FileStatus.READY,
        uuid: fileUuid,
        name: fileName,
        url: getSuccessFileUrl(fileUuid, fileName),
      };
    }
  } catch (error) {
    logger.error(error);
  }

  return { status: FileStatus.FAILED };
}

export async function pollScanningStatus(uuid: string) {
  return new Promise((resolve, reject) => {
    PollingUtils.pollingWithFibonacciInterval(
      uuid,
      FileUploadApi.getVirusScanningStatus,
      (response) =>
        !(response.code === "ok" && response.data && response.data.status !== FileStatus.SCANNING),
      Date.now(),
      300000,
      1000,
      2000,
    ).then(resolve, reject);
  });
}

const FileTypeToMediaType: Record<FileTypeValues, MediaType> = {
  [FileType.AUDIO]: MEDIA_TYPE.AUDIO,
  [FileType.VIDEO]: MEDIA_TYPE.VIDEO,
  [FileType.IMAGE]: MEDIA_TYPE.IMAGE,
  [FileType.PDF]: MEDIA_TYPE.PDF,
  [FileType.WORD]: MEDIA_TYPE.WORD,
  [FileType.EXCEL]: MEDIA_TYPE.EXCEL,
  [FileType.POWERPOINT]: MEDIA_TYPE.POWERPOINT,
  [FileType.ZIP]: MEDIA_TYPE.ZIP,
  [FileType.CSV]: MEDIA_TYPE.OTHERS,
  [FileType.VTT]: MEDIA_TYPE.OTHERS,
  [FileType.ICS]: MEDIA_TYPE.OTHERS,
  [FileType.OTHERS]: MEDIA_TYPE.OTHERS,
};

export function useFileUploader(
  uploadFlow: Ref<UploadFlowValues>,
  emit,
  fileEntry: Ref<FileUploadEntry | null>,
  recordType?: Ref<string | undefined>,
  recordUuid?: Ref<string | undefined>,
) {
  const fileResponse = ref<FileResponse | null>(null);
  const { t } = useI18n();
  const ACRONYM_FILE_TYPES = [FileType.PDF, FileType.CSV, FileType.ZIP, FileType.ICS];

  function getFormattedAllowedFileTypes(): string[] {
    const allowedTypes = UploadFlowFileTypeMap[uploadFlow.value] || [];
    return allowedTypes
      .map((type) => {
        const limits = FileTypeMap[type];
        if (!limits) return "";

        // Uppercase only for acronyms (PDF, ZIP, CSV); others use title case
        const formattedType = ACRONYM_FILE_TYPES.includes(type)
          ? type
          : StringUtils.toTitleCase(type);

        // Extensions lowercase, ensure each starts with a dot
        const extensions = limits.ext
          .map((ext) => `.${ext.replace(/^\./, "").toLowerCase()}`)
          .join(", ");

        const sizeMB = (limits.size / (1024 * 1024)).toFixed(0);

        return `<span>${formattedType}</span>: ${extensions} (${sizeMB} MB)`;
      })
      .filter(Boolean);
  }

  function setFileTypeError() {
    fileResponse.value = {
      errorType: FileValidationErrorType.FILE_TYPE,
      errorTitle: t("file_upload_invalid_file_type_title"),
      errorMessage: [t("file_upload_invalid_file_type_message"), ...getFormattedAllowedFileTypes()],
    };
  }

  function setFileSizeError() {
    fileResponse.value = {
      errorType: FileValidationErrorType.FILE_SIZE,
      errorTitle: t("file_upload_invalid_file_size_title"),
      errorMessage: [t("file_upload_invalid_file_size_message"), ...getFormattedAllowedFileTypes()],
    };
  }

  function getFileType(file: File): FileTypeValues {
    const mediaType = file.type; // full mime, e.g. "audio/mpeg"
    const extension = getFileExtension(file.name);

    // Match by MIME type first
    if (mediaType) {
      for (const type of FileTypeOrder) {
        const config = FileTypeMap[type];
        if (config.mime.includes(mediaType)) {
          return type;
        }
      }
    }

    // Fallback to extension match
    if (extension) {
      for (const type of FileTypeOrder) {
        const config = FileTypeMap[type];
        if (config.ext.includes(extension)) {
          return type;
        }
      }
    }

    // Default
    return FileType.OTHERS;
  }

  function getMediaType(file: File): MediaType {
    const fileType = getFileType(file);
    return FileTypeToMediaType[fileType] || MEDIA_TYPE.OTHERS;
  }

  const validateFile = async (file: FileUploadEntry) => {
    if (!file || !file.file) {
      fileResponse.value = {
        errorType: FileValidationErrorType.EMPTY_FILE,
        errorTitle: t("file_upload_validation_empty_file_message"),
      };
      emit(fileResponse.value);
      return false;
    }

    const extension = getFileExtension(file.file.name).toLowerCase();
    const mimeType = file.file.type;
    const fileSize = file.file.size;
    const allowedTypes = UploadFlowFileTypeMap[uploadFlow.value];
    const fileType = getFileType(file.file);

    if (!allowedTypes || !allowedTypes.includes(fileType)) {
      setFileTypeError();
      emit(fileResponse.value);
      return false;
    }

    const config = FileTypeMap[fileType];
    if (!config.ext.includes(extension.toLowerCase())) {
      setFileTypeError();
      emit(fileResponse.value);
      return false;
    }

    if (mimeType && !config.mime.includes(mimeType.toLowerCase())) {
      setFileTypeError();
      emit(fileResponse.value);
      return false;
    }

    if (fileSize > config.size) {
      setFileSizeError();
      emit(fileResponse.value);
      return false;
    }

    if (fileType === FileType.IMAGE) {
      const valid = await validateImageDimension(file.file);
      if (!valid) {
        fileResponse.value = {
          errorType: FileValidationErrorType.IMAGE_DIMENSION,
          errorTitle: t("file_upload_image_file_dimension_title"),
          errorMessage: [t("file_upload_image_file_dimension_message")],
        };
        emit(fileResponse.value);
        return false;
      }
    }
    return true;
  };

  const validateImageDimension = (file) => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = URL.createObjectURL(file);
      image.onload = () => {
        resolve(image.height <= Dimensions.limit.height && image.width <= Dimensions.limit.width);
      };
      image.onerror = () => {
        resolve(true);
      };
    });
  };

  const uploadFile = async () => {
    if (!fileEntry.value || !fileEntry.value.file) {
      fileResponse.value = {
        errorType: FileValidationErrorType.EMPTY_FILE,
        errorTitle: t("file_upload_validation_empty_file_message"),
        status: FileStatus.FAILED,
      };
      emit(fileResponse.value);
      return;
    }

    if (fileEntry.value.isValid) {
      try {
        const result = await uploadFileToS3(fileEntry.value.file, uploadFlow.value, {
          recordType: recordType?.value,
          recordUuid: recordUuid?.value,
        });
        fileResponse.value =
          result.status === FileStatus.READY
            ? { status: result.status, uuid: result.uuid, name: result.name, url: result.url }
            : { status: FileStatus.FAILED };
      } catch {
        fileResponse.value = { status: FileStatus.FAILED };
      }

      if (
        !fileEntry.value.isValid ||
        (fileResponse.value && fileResponse.value.status === FileStatus.FAILED)
      ) {
        fileResponse.value = {
          status: FileStatus.FAILED,
          errorTitle: t("file_upload_generic_error_title"),
          errorMessage: [t("fetch_data_button_error_message")],
        };
        fileEntry.value = null;
      }
      emit(fileResponse.value);
    }
  };
  return {
    validateFile,
    uploadFile,
    getFileType,
    getMediaType,
  };
}
